import { RichTextEditorProps } from "molecules/rich-text/rich-text-editor";
import React, { useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import useQuill from "utilities/hooks/quill/use-quill";
import { NotebookTextBlockQuillConfig } from "utilities/quill/quill-config";
import katex from "katex";
import "katex/dist/katex.min.css";
import RichTextEditorFooter from "molecules/rich-text/rich-text-editor-footer";
import NotebookTextBlockRichTextEditorToolbar from "organisms/notebook-text-block-rich-text-editor/notebook-text-block-rich-text-editor-toolbar";

// Typescript solution for Katex (math renderer)
declare global {
    interface Window {
        katex: any;
    }
}

interface NotebookTextBlockRichTextEditorProps extends RichTextEditorProps {
    onDelete: () => Promise<void>;
    onMoveUp: () => Promise<void>;
    onMoveDown: () => Promise<void>;
    onDone?: () => Promise<void>;
    maxLength?: number;
}

const CSS_CLASS_NAME = "c-text-block-rich-text-editor";

// Registers modules
NotebookTextBlockQuillConfig.registerCustomModules();

const NotebookTextBlockRichTextEditor = (
    props: NotebookTextBlockRichTextEditorProps
) => {
    const {
        disabled,
        errors,
        maxLength,
        onBlur,
        placeholder,
        preserveWhitespace,
        value,
        onDelete,
        onDone,
        onMoveDown,
        onMoveUp,
    } = props;

    // Allows math to render
    window.katex = katex;

    const reactQuillRef = useRef<ReactQuill>(null);

    const {
        className,
        containerId,
        containerSelector,
        handleChange,
        handleFocus,
        handleSelectionChange,
        quillModules,
        toolbarId,
        plainTextValue,
    } = useQuill(CSS_CLASS_NAME, props, reactQuillRef);

    useEffect(() => {
        if (
            reactQuillRef.current &&
            !disabled &&
            process.env.NODE_ENV !== "test"
        ) {
            const quill = reactQuillRef.current.getEditor();
            const length = quill.getLength();
            if (length > 1) {
                quill.setSelection(length - 1, 0);
            } else {
                quill.focus();
            }
        }
    }, [disabled]);

    // ReactQuill adds a training \n, so subtract 1
    const plainTextLength = (plainTextValue.length ?? 1) - 1;

    const handleTextChange = (
        value: string,
        delta,
        source,
        editor: ReactQuill.UnprivilegedEditor
    ) => {
        const currentPlainTextLength =
            (reactQuillRef.current?.getEditor()?.getText().length ?? 1) - 1;

        const isPastMax = maxLength && currentPlainTextLength > maxLength;
        if (isPastMax) {
            const amountOverLimit = currentPlainTextLength - maxLength;

            if (amountOverLimit) {
                reactQuillRef.current
                    ?.getEditor()
                    ?.deleteText(maxLength, amountOverLimit);
                return;
            }
        }
        handleChange(value, delta, source, editor);
    };

    return (
        <div className={className}>
            <NotebookTextBlockRichTextEditorToolbar
                allowImages={false}
                allowReferenceLinks={false}
                disabled={false}
                id={toolbarId}
                onDeleteClick={onDelete}
                onDone={onDone}
                onMoveDown={onMoveDown}
                onMoveUp={onMoveUp}
            />
            <ReactQuill
                bounds={containerSelector}
                className={`${CSS_CLASS_NAME}__editor`}
                formats={NotebookTextBlockQuillConfig.notebookQuillFormats}
                id={containerId}
                modules={quillModules}
                onBlur={onBlur}
                onChange={handleTextChange}
                onChangeSelection={handleSelectionChange}
                onFocus={handleFocus}
                placeholder={placeholder}
                preserveWhitespace={preserveWhitespace}
                readOnly={disabled}
                ref={reactQuillRef}
                theme={NotebookTextBlockQuillConfig.theme}
                value={value}
            />
            <RichTextEditorFooter
                baseClassName={CSS_CLASS_NAME}
                currentLength={plainTextLength}
                errors={errors}
                maxLength={maxLength}
            />
        </div>
    );
};

export default NotebookTextBlockRichTextEditor;
